import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { translate } from 'utils/translations'
import { flashSubSidebar } from 'utils'
import { T } from 'consts/translationKeys'
import UploadIcon from 'assets/svgr-icons/Media.svg'
import AIImageIcon from 'assets/svgr-icons/AIimage.svg'
import AIVideoIcon from 'assets/svgr-icons/AI_Video.svg'
import { SUB_SIDEBAR_KEYS } from 'consts/sidebar'
import { dispatch } from 'store'
import { editorThunks } from 'store/features/editor/thunks'
import { useDropdownState } from 'hooks/useDropdownState'
import { SMALL_DESKTOP_SIZE_BREAKPOINT } from 'consts'
import { useResponsive } from 'hooks/useResponsive'
import { aiImageThunks } from 'store/features/aiImage/thunks'
import { aiImageSelectors } from 'store/features/aiImage/selectors'
import { aiVideoThunks } from 'store/features/aiVideo/thunks'
import { aiVideoSelectors } from 'store/features/aiVideo/selectors'
import { useIsTabActive } from 'hooks/useIsTabActive'
import { AI_GENERATE_TAB_KEY } from 'containers/SubSidebar/AiImageContent/consts'
import { TabKey } from 'containers/SubSidebar/AIVideoContent/consts'

export const usePlusButtonActions = (onUpload?: () => void) => {
  const { closeDropdown } = useDropdownState()
  const isMobile = useResponsive(SMALL_DESKTOP_SIZE_BREAKPOINT)

  const isTabActive = useIsTabActive()

  const openSubSidebar = useCallback(
    (tabKey: string) => {
      dispatch(editorThunks.setActiveSidebarTabAndCloseOverlay(tabKey))
      closeDropdown()

      if (isMobile) {
        dispatch(editorThunks.setIsMobileSubSidebarOpen(true))
      }
    },
    [closeDropdown, isMobile]
  )

  // Detect if target forms have any data populated (used to reset on regenerate)
  const targetAiImageMediaItemId = useSelector(
    aiImageSelectors.targetMediaItemId
  )

  const targetAiVideoMediaItemId = useSelector(
    aiVideoSelectors.targetMediaItemId
  )

  const handleFlashSubSidebar = useCallback(
    (tabKey: string) => {
      if (isTabActive(tabKey)) {
        flashSubSidebar()
      }
    },
    [isTabActive]
  )

  const handleTabAction = useCallback(
    (tabKey: string, resetAction: () => void) => {
      const targetIsActive = isTabActive(tabKey)
      const targetHasData =
        tabKey === SUB_SIDEBAR_KEYS.AI_IMAGE
          ? Boolean(targetAiImageMediaItemId)
          : Boolean(targetAiVideoMediaItemId)
      const isRegenerateCase = targetIsActive && targetHasData

      // Same tab + has data: regenerate -> reset, no flash
      if (isRegenerateCase) {
        resetAction()
        openSubSidebar(tabKey)
        return
      }

      // Same tab + clean state: flash and open
      if (targetIsActive) {
        handleFlashSubSidebar(tabKey)
        openSubSidebar(tabKey)
        return
      }

      // Switching tabs without unsaved changes: reset target before open
      resetAction()
      openSubSidebar(tabKey)
    },
    [
      isTabActive,
      targetAiImageMediaItemId,
      targetAiVideoMediaItemId,
      handleFlashSubSidebar,
      openSubSidebar
    ]
  )

  const actions = useMemo(
    () => [
      {
        key: 'upload',
        title: translate(T.UPLOAD_LABEL_SIDEBAR),
        icon: <UploadIcon />,
        onClick: () => onUpload?.()
      },
      {
        key: 'ai-image',
        title: translate(T.AI_IMAGE_LABEL),
        icon: <AIImageIcon />,
        onClick: () => {
          dispatch(aiImageThunks.setActiveTab(AI_GENERATE_TAB_KEY))
          handleTabAction(SUB_SIDEBAR_KEYS.AI_IMAGE, () =>
            dispatch(aiImageThunks.resetGenerateForm())
          )
        }
      },
      {
        key: 'ai-video',
        title: translate(T.AI_VIDEO_LABEL),
        icon: <AIVideoIcon />,
        onClick: () => {
          dispatch(aiVideoThunks.setActiveTab(TabKey.GENERATE))
          handleTabAction(SUB_SIDEBAR_KEYS.AI_VIDEO, () =>
            dispatch(aiVideoThunks.clearGenerationParams())
          )
        }
      }
    ],
    [handleTabAction, onUpload]
  )

  return actions
}
